// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect } from 'react'

import { Button, Input } from '@/components/ui'
import { Search, X } from 'lucide-react'

import type { ItemFilterState, KnowledgeBaseItem } from '../types'

interface ItemFiltersProps {
  filters: ItemFilterState
  items: KnowledgeBaseItem[]
  totalItems: number
  onTextChange: (value: string) => void
}

export function ItemFilters({
  filters,
  items,
  totalItems,
  onTextChange,
}: ItemFiltersProps): JSX.Element {
  // Local state for immediate input feedback, debounced to parent
  const [localText, setLocalText] = useState(filters.text)

  // Sync from parent when filters change externally
  useEffect(() => {
    setLocalText(filters.text)
  }, [filters.text])

  // Debounce text changes to parent
  useEffect(() => {
    if (localText === filters.text) return
    const timer = setTimeout(() => onTextChange(localText), 200)
    return () => clearTimeout(timer)
  }, [localText, filters.text, onTextChange])

  return (
    <div className="mb-4 space-y-3">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search guidance items..."
            value={localText}
            onChange={event => setLocalText(event.target.value)}
            className="pl-10"
            data-testid="kb-search"
          />
          {localText && (
            <Button
              aria-label="Clear search"
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              onClick={() => setLocalText('')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <span className="text-sm text-muted-foreground" data-testid="kb-filtered-count">
          {items.length === totalItems ? totalItems : `${items.length} of ${totalItems}`} {totalItems === 1 ? 'item' : 'items'}
        </span>
      </div>

      {filters.text && (
        <p className="text-sm text-muted-foreground">
          Showing results matching "{filters.text}".
        </p>
      )}
    </div>
  )
}
